/**
 * Shared text formatting utilities to prevent layout breaking issues
 * when applying formatting to contentEditable elements
 */

/**
 * Sanitizes HTML content to only allow safe formatting tags
 * @param htmlContent - The HTML content to sanitize
 * @returns Cleaned HTML content with only safe formatting tags
 */
export const sanitizeHTML = (htmlContent: string): string => {
  // Only allow safe formatting tags, list tags, heading tags, and line breaks
  const allowedTags = ['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span', 'font', 'ul', 'ol', 'li', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Remove any tags that aren't in the allowed list
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    if (!allowedTags.includes(element.tagName.toLowerCase()) && element.tagName !== 'DIV') {
      // Replace disallowed tags with their text content
      const textContent = element.textContent || '';
      element.replaceWith(textContent);
    } else {
      // For allowed tags, clean up attributes but preserve essential ones
      // Preserve highlight-related attributes on span elements
      if (element.tagName.toLowerCase() === 'span') {
        const allowedAttributes = ['class', 'data-highlight-id', 'data-highlight-type', 'data-highlight-name', 'style'];
        const attributesToRemove: string[] = [];
        
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          if (!allowedAttributes.includes(attr.name)) {
            attributesToRemove.push(attr.name);
          }
        }
        
        attributesToRemove.forEach(attrName => element.removeAttribute(attrName));
      } else if (element.tagName.toLowerCase() === 'font') {
        // Preserve color attribute on font tags
        const allowedAttributes = ['color', 'style'];
        const attributesToRemove: string[] = [];
        
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          if (!allowedAttributes.includes(attr.name)) {
            attributesToRemove.push(attr.name);
          }
        }
        
        attributesToRemove.forEach(attrName => element.removeAttribute(attrName));
      } else {
        // For other tags, remove all attributes except style
        const attributesToRemove: string[] = [];
        
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          if (attr.name !== 'style') {
            attributesToRemove.push(attr.name);
          }
        }
        
        attributesToRemove.forEach(attrName => element.removeAttribute(attrName));
      }
    }
  });
  
  return tempDiv.innerHTML;
};

/**
 * Normalizes HTML content to ensure proper text flow
 * @param htmlContent - The HTML content to normalize
 * @returns Normalized HTML content with proper text flow
 */
export const normalizeContent = (htmlContent: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Ensure proper text flow for contentEditable
  const textNodes = [];
  const walker = document.createTreeWalker(
    tempDiv,
    NodeFilter.SHOW_TEXT,
    null
  );
  
  let node;
  while ((node = walker.nextNode()) !== null) {
    textNodes.push(node);
  }
  
  // Normalize text nodes to prevent layout issues
  // But preserve line breaks that are meaningful
  textNodes.forEach(textNode => {
    if (textNode.textContent) {
      // Replace multiple spaces/tabs with single space, but preserve intentional line structure
      textNode.textContent = textNode.textContent.replace(/[ \t]+/g, ' ');
    }
  });
  
  return tempDiv.innerHTML;
};

/**
 * Applies formatting to selected text using document.execCommand
 * and then cleans up the result to prevent layout issues
 * @param command - The formatting command to apply
 * @param value - Optional value for the command
 * @param editableElement - The contentEditable element
 * @returns The cleaned and normalized HTML content
 */
export const applyFormatting = (
  command: string, 
  value?: string, 
  editableElement?: HTMLElement
): string => {
  try {
    // Handle list commands specially to split lines into separate list items
    if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedContent = range.cloneContents();
        
        // Get the text content and HTML content
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(selectedContent);
        const htmlContent = tempDiv.innerHTML;
        const textContent = tempDiv.textContent || '';
        
        // Check if there are line breaks (newlines, <br> tags, or multiple lines)
        const hasLineBreaks = textContent.includes('\n') || 
                             htmlContent.includes('<br') || 
                             htmlContent.includes('<BR');
        
        if (hasLineBreaks) {
          // Split by line breaks while preserving HTML (including highlights)
          // We need to split the HTML content, not just the text
          let processedHtml = htmlContent;
          
          console.log('📋 Creating list from HTML:', processedHtml);
          
          // Replace newlines with <br> tags if they exist as plain text
          processedHtml = processedHtml.replace(/\n/g, '<br>');
          
          // Split by <br> tags while preserving HTML structure
          const lines: string[] = [];
          const splitRegex = /<br\s*\/?>/gi;
          const parts = processedHtml.split(splitRegex);
          
          console.log('📋 Split into parts:', parts);
          
          // Process each part to extract non-empty lines with their HTML intact
          parts.forEach((part, index) => {
            // Don't use innerHTML/textContent as it might strip or modify the HTML
            // Instead, just check if there's meaningful content
            const trimmedPart = part.trim();
            
            if (trimmedPart.length > 0) {
              // Create a temp element to check if there's actual text content
              const tempSpan = document.createElement('span');
              tempSpan.innerHTML = trimmedPart;
              const textContent = (tempSpan.textContent || '').trim();
              
              // Only add if there's actual text (not just empty tags)
              if (textContent.length > 0) {
                console.log(`📋 Line ${index}: "${textContent}" with HTML:`, trimmedPart);
                lines.push(trimmedPart);
              }
            }
          });
          
          if (lines.length > 1 && editableElement) {
            // Create the list structure manually, preserving HTML in each line
            const listTag = command === 'insertUnorderedList' ? 'ul' : 'ol';
            const listHtml = `<${listTag}>${lines.map(line => `<li>${line}</li>`).join('')}</${listTag}>`;
            
            // Sanitize and normalize the list HTML before inserting
            // This ensures we only have one operation in the undo stack
            const sanitizedListHtml = sanitizeHTML(listHtml);
            const normalizedListHtml = normalizeContent(sanitizedListHtml);
            
            // Use insertHTML execCommand to replace the selection in a single atomic operation
            // This ensures proper undo/redo behavior without duplication
            document.execCommand('insertHTML', false, normalizedListHtml);
            
            // Return the final content
            return editableElement.innerHTML;
          }
        }
      }
      
      // If no line breaks or only single line, use native command
      document.execCommand(command, false);
    }
    // Apply the formatting for other commands
    else if (command === 'formatBlock' && value) {
      document.execCommand('formatBlock', false, value);
    } else if (command === 'foreColor' && value) {
      // Use styleWithCSS for better compatibility and modern inline styles
      document.execCommand('styleWithCSS', false, 'true');
      document.execCommand('foreColor', false, value);
    } else if (command === 'hiliteColor' && value) {
      document.execCommand('hiliteColor', false, value);
    } else if (command === 'bold') {
      document.execCommand('bold', false);
    } else if (command === 'italic') {
      document.execCommand('italic', false);
    } else if (command === 'underline') {
      document.execCommand('underline', false);
    } else if (command === 'strikethrough') {
      document.execCommand('strikethrough', false);
    } else if (command === 'justifyLeft' || command === 'justifyCenter' || command === 'justifyRight') {
      document.execCommand(command, false);
    } else {
      document.execCommand(command, false);
    }
    
    // Get the updated content
    const updatedContent = editableElement?.innerHTML || '';
    
    // Clean up any potential layout issues
    const sanitizedContent = sanitizeHTML(updatedContent);
    const normalizedContent = normalizeContent(sanitizedContent);
    
    // Update the element if provided
    if (editableElement) {
      editableElement.innerHTML = normalizedContent;
    }
    
    return normalizedContent;
  } catch (error) {
    console.error('Formatting error:', error);
    return editableElement?.innerHTML || '';
  }
};

/**
 * Processes HTML content to ensure it's clean and properly formatted
 * @param htmlContent - The HTML content to process
 * @returns Processed HTML content that's safe and properly formatted
 */
export const processHTMLContent = (htmlContent: string): string => {
  // Only convert \n characters to <br> tags if they're not already <br> tags
  // This prevents double conversion and malformed HTML
  let processedContent = htmlContent;
  
  // Only convert standalone \n characters that aren't part of existing HTML
  if (!processedContent.includes('<br>') && !processedContent.includes('<BR>')) {
    processedContent = processedContent.replace(/\n/g, '<br>');
  }
  
  const sanitizedContent = sanitizeHTML(processedContent);
  return normalizeContent(sanitizedContent);
};

/**
 * Processes HTML content with automatic highlighting
 * @param htmlContent - The HTML content to process
 * @returns Processed HTML content with highlights applied
 */
export const processHTMLContentWithHighlights = async (htmlContent: string): Promise<string> => {
  // First process normally
  const processedContent = processHTMLContent(htmlContent);
  
  // Then apply automatic highlighting
  return applyAutomaticHighlights(processedContent);
};

/**
 * Applies automatic highlighting to HTML content
 * @param htmlContent - The HTML content to highlight
 * @returns HTML content with automatic highlights
 */
const applyAutomaticHighlights = async (htmlContent: string): Promise<string> => {
  try {
    // Dynamic import to avoid circular dependencies
    const { textHighlightingService } = await import('../services/TextHighLightingService');
    
    // Create a temporary DOM element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Process text nodes for highlighting
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    const textNodes: Text[] = [];
    let node;
    while ((node = walker.nextNode()) !== null) {
      textNodes.push(node as Text);
    }
    
    // Apply highlights to each text node
    textNodes.forEach(textNode => {
      const textContent = textNode.textContent || '';
      const highlightedResult = textHighlightingService.processText(textContent);
      
      if (highlightedResult.highlightedHTML !== textContent) {
        // Replace text node with highlighted HTML
        const span = document.createElement('span');
        span.innerHTML = highlightedResult.highlightedHTML;
        textNode.parentNode?.replaceChild(span, textNode);
      }
    });
    
    return tempDiv.innerHTML;
  } catch (error) {
    console.error('Error applying automatic highlights:', error);
    return htmlContent; // Return original content if highlighting fails
  }
};
